import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Injector, Input, OnChanges, OnInit, SimpleChanges, ViewChild, inject } from '@angular/core'
import {
  ControlContainer,
  ControlValueAccessor,
  FormControl,
  FormControlDirective,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule
} from '@angular/forms'
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {endOfDay, format, isDate, isValid, parseISO, startOfDay} from "date-fns";
import {Calendar} from "primeng/calendar";
import {TooltipModule} from "ngx-bootstrap/tooltip";
import {DatePickerModule} from "primeng/datepicker";
import {dateToIsoDateStringWithCorrectTimezone} from '../../utils/date-to-iso-date-string-with-correct-timezone';
import {LocaleService} from '../../services/locale-service/locale.service';
import {KeyValuePipe, NgForOf, NgIf} from '@angular/common';
import {fromZonedTime, toZonedTime} from 'date-fns-tz';

export enum TIME_MODE {
  START_OF_DAY = 'START_OF_DAY',
  END_OF_DAY = 'END_OF_DAY',
  SET_TIME = 'SET_TIME'
}

@Component({
  selector: 'app-date-time',
  templateUrl: './date-time-selector.component.html',
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: DateTimeSelectorComponent,
    multi: true
  }],
  imports: [TooltipModule, DatePickerModule, ReactiveFormsModule, NgIf, TranslatePipe, NgForOf, KeyValuePipe],
  standalone: true
})
export class DateTimeSelectorComponent implements ControlValueAccessor, OnChanges, OnInit {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly injector = inject(Injector);
  private readonly translate = inject(TranslateService);
  private readonly localeService = inject(LocaleService);


  @Input() public label!: string
  @Input() public small = false

  @Input() public inline = false

  @Input() public errorMessages!: Record<string, string>
  @Input() public prefix?: string


  @ViewChild('datePicker') public datePicker?: Calendar;

  @Input() public tooltipText?: string

  @Input() public minDate?: Date | null;

  @Input() public maxDate?: Date | null;

  @Input() public placeholder = ''

  @Input() public timeEnabled = false;
  public locale: "de" | "en";

  @Input() public timeMode: TIME_MODE = TIME_MODE.START_OF_DAY;


  public internalControl = new FormControl<Date | null>(null)

  @ViewChild(FormControlDirective, {static: true})
  public formControlDirective?: FormControlDirective;

  @Input()
  public formControl?: FormControl<string | null>;

  @Input()
  public formControlName?: string;

  @Input()
  public name?: string;

  @Input()
  public customOutputFormat?: string;
  public dateFormat = 'dd.mm.yy'
  protected readonly TIME_MODE = TIME_MODE;
  protected readonly Intl = Intl;

  @Input() public defaultDate: Date = new Date();

  constructor() {
    this.locale = this.localeService.currentLocale;
    this.localeService.localeChanged.subscribe(
      (l) => this.locale = l
    )
  }

  public get control(): FormControl {
    if (this.formControl) {
      return this.formControl
    }
    const controlCont = this.controlContainer
    if (this.formControlName && controlCont?.control !== null) {
      return controlCont?.control.get(this.formControlName) as FormControl;
    }
    return new FormControl(null)

  }

  get controlContainer(): ControlContainer {
    return this.injector.get(ControlContainer);
  }

  public ngOnInit() {
    if (this.customOutputFormat && this.formControl) {
      this.formControl?.setValue(this.applyCustomFormat(this.formControl.value ?? ''));

      this.formControl.valueChanges.subscribe(value => {
        this.formControl?.setValue(this.applyCustomFormat(value ?? ''), {emitEvent: false});
      });
    }
  }

  public ngOnChanges(changes: SimpleChanges) {

    if (
      this.datePicker &&
      this.minDate &&
      changes['minDate']?.previousValue &&
      changes['minDate'].previousValue.toISOString() !== this.minDate.toISOString()
    ) {
      this.datePicker._minDate = this.minDate;
      this.cdr.markForCheck();
    }

    if (
      this.datePicker &&
      this.maxDate &&
      changes['maxDate']?.previousValue &&
      changes['maxDate'].previousValue.toISOString() !== this.maxDate.toISOString()
    ) {
      this.datePicker.maxDate = this.maxDate;
      this.cdr.markForCheck();
    }
  }

  public onTouched = () => {
    //
  }

  public onChange = (val: unknown) => {
    console.warn('on change not yet registered in date-time-selector', {val})
  }

  public inputChanged(event: { target: { value: string } }) {
    if (this.datePicker && !this.datePicker.overlayVisible) {
      this.datePicker.toggle();
    }

    const quickDateTimeRegex = /^(0[1-9]|[12][0-9]|3[01])(0[1-9]|1[0-2])(\d{4})([01][0-9]|2[0-3])([0-5][0-9])$/;
    const quickDateRegex = /^(0[1-9]|[12][0-9]|3[01])(0[1-9]|1[0-2])(\d{4})$/;

    const enDateTimeRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/(\d{4}) ([01][0-9]|2[0-3]):([0-5][0-9])$/;
    const enDateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/(\d{4})$/;

    const deDateTimeRegex = /^(0[1-9]|[12][0-9]|3[01])\.(0[1-9]|1[0-2])\.(\d{4}) ([01][0-9]|2[0-3]):([0-5][0-9])$/;
    const deDateRegex = /^(0[1-9]|[12][0-9]|3[01])\.(0[1-9]|1[0-2])\.(\d{4})$/;


    const inputValue = event.target?.value;

    let date: Date | undefined = undefined;

    if (this.timeMode === TIME_MODE.SET_TIME) {
      if (quickDateTimeRegex.test(inputValue)) {
        const [, day, month, year, hour, minute] = inputValue.match(quickDateTimeRegex) as RegExpMatchArray;
        date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hour), parseInt(minute));
      } else if (deDateTimeRegex.test(inputValue)) {
        const [, day, month, year, hour, minute] = inputValue.match(deDateTimeRegex) as RegExpMatchArray;
        date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hour), parseInt(minute));
      } else if (enDateTimeRegex.test(inputValue)) {
        const [, month, day, year, hour, minute] = inputValue.match(enDateTimeRegex) as RegExpMatchArray;
        date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hour), parseInt(minute));
      }

      date?.setSeconds(0);

    } else {
      if (quickDateRegex.test(inputValue)) {
        const [, day, month, year] = inputValue.match(quickDateRegex) as RegExpMatchArray;
        date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      } else if (deDateRegex.test(inputValue)) {
        const [, day, month, year] = inputValue.match(deDateRegex) as RegExpMatchArray;
        date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      } else if (enDateRegex.test(inputValue)) {
        const [, month, day, year] = inputValue.match(enDateRegex) as RegExpMatchArray;
        date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      }
    }

    if (date && this.timeMode === TIME_MODE.START_OF_DAY) {
      date = startOfDay(date);
    }
    if (date && this.timeMode === TIME_MODE.END_OF_DAY) {
      date = endOfDay(date);
    }

    if (date && isDate(date) && isValid(date) && this.datePicker?.isValidDate(date) && this.datePicker.isValidSelection(date)) {
      this.datePicker.initTime(date);
      this.datePicker.selectDate({year: date.getFullYear(), month: date.getMonth(), day: date.getDate()});
      this.datePicker.hideOverlay()
      this.internalControl.setValue(date);

      this.convertAndEmitToOnChange(date);
      this.cdr.markForCheck();
    } else {
      this.convertAndEmitToOnChange(undefined)
    }
  }

  public dateSelected = (date: Date | undefined) => {
    if (date === undefined) {
      this.onChange(undefined)
      return;
    }

    if (this.timeMode === TIME_MODE.START_OF_DAY) {
      date = startOfDay(date)
    } else if (this.timeMode === TIME_MODE.END_OF_DAY) {
      date = endOfDay(date)
    }

    this.internalControl.setValue(date);
    this.convertAndEmitToOnChange(date);
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  registerOnChange(fn: (val: unknown) => void): void {
    this.onChange = fn;
  }

  writeValue(dateString: string): void {

    // datString is in UTC, we need to convert it to our display timezone, which is Europe/Berlin
    const date = toZonedTime(parseISO(dateString), 'Europe/Berlin');

    if (isDate(date) && isValid(date)) {
      this.internalControl.setValue(date);
    } else if (this.internalControl.value !== null) {
      this.internalControl.setValue(null);

    }
  }

  setDisabledState(isDisabled: boolean): void {
    // da formControl und control identisch sind brauchen wir hier nichts weiter zu geben
    if (isDisabled) {
      this.internalControl.disable();
    } else {
      this.internalControl.enable();
    }
  }

  private convertAndEmitToOnChange(date: Date | undefined) {
    if (!date) {
      this.onChange(undefined);
      return;
    }
    // wir müssen das date als Europe/Berlin interpretieren

    date.setSeconds(0, 0);

    const outputDate = this.customOutputFormat ? this.applyCustomFormat(date.toISOString()) : dateToIsoDateStringWithCorrectTimezone(date);
    this.onChange(outputDate);
  }

  private applyCustomFormat(value?: string): string|null {
    if (!value) return null;
    if (!this.customOutputFormat) return value;

    return format(fromZonedTime(parseISO(value), 'Europe/Berlin'), this.customOutputFormat);
  }
}
